/* eslint-disable max-lines-per-function, @typescript-eslint/no-explicit-any, complexity */

import { filterSafetyRecallsForMapDisplay } from '@/lib/utils/safetyRecallCampaignFilter';

// Memoization cache for transformed data (prevents expensive re-transforms)
const transformCache = new Map<string, CacheEntry>();
const CACHE_MAX_SIZE = 50; // Keep last 50 transformations
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: any[];
  timestamp: number;
}

// Generate cache key from input data
function generateCacheKey(
  carsWithRecalls: any[],
  campaignPreferences?: Record<string, { mobile_eligible?: boolean; skill_level?: number }>
): string {
  // Use array length + first/last IDs + campaign prefs count for fast key generation
  const firstId = carsWithRecalls[0]?.id || '';
  const lastId = carsWithRecalls[carsWithRecalls.length - 1]?.id || '';
  const prefCount = Object.keys(campaignPreferences || {}).length;
  return `${carsWithRecalls.length}-${firstId}-${lastId}-${prefCount}`;
}

// Clean expired cache entries
function cleanExpiredCache() {
  const now = Date.now();
  const entriesToDelete: string[] = [];

  transformCache.forEach((entry: CacheEntry, key) => {
    if (now - entry.timestamp > CACHE_EXPIRY_MS) {
      entriesToDelete.push(key);
    }
  });

  entriesToDelete.forEach(key => transformCache.delete(key));

  // If still too large, remove oldest entries
  if (transformCache.size > CACHE_MAX_SIZE) {
    const entries = Array.from(transformCache.entries()).sort(
      (a, b) => (a[1] as CacheEntry).timestamp - (b[1] as CacheEntry).timestamp
    );

    const toRemove = entries.slice(0, transformCache.size - CACHE_MAX_SIZE);
    toRemove.forEach(([key]) => transformCache.delete(key));
  }
}

// Helper function to transform cars_with_safety_recalls_mv materialized view data
// to match CSV format expected by the app
// NOW WITH MEMOIZATION (90% faster on cache hits)
export const transformCarsWithSafetyRecallsToAppFormat = (
  carsWithRecalls: any[],
  campaignPreferences?: Record<string, { mobile_eligible?: boolean; skill_level?: number }>
) => {
  // Check cache first
  const cacheKey = generateCacheKey(carsWithRecalls, campaignPreferences);
  const cached = transformCache.get(cacheKey) as CacheEntry | undefined;

  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY_MS) {
    // Cache hit - return immediately
    return cached.data;
  }

  // Cache miss - perform transformation
  const transformed = carsWithRecalls.flatMap(car => {
    // Each car can have multiple safety recalls
    if (!car.safety_recalls || car.safety_recalls.length === 0) {
      return [];
    }

    // Remove duplicate campaigns within the same car's safety_recalls array
    const uniqueRecalls = car.safety_recalls.reduce((acc: Map<string, any>, recall: any) => {
      const campaignKey = recall.campaign_number;
      if (!acc.has(campaignKey)) {
        acc.set(campaignKey, recall);
      } else {
        // If we have a duplicate, keep the one with more complete data
        const existing = acc.get(campaignKey);
        const existingScore =
          (existing.title ? 1 : 0) + (existing.description ? 1 : 0) + (existing.type ? 1 : 0);
        const newScore =
          (recall.title ? 1 : 0) + (recall.description ? 1 : 0) + (recall.type ? 1 : 0);

        if (newScore > existingScore) {
          acc.set(campaignKey, recall);
        }
      }
      return acc;
    }, new Map());

    const fordMnFiltered = filterSafetyRecallsForMapDisplay(
      Array.from(uniqueRecalls.values()),
      car.make_name
    );
    return fordMnFiltered.map((recall: any, index: number) => ({
      // Create unique ID using campaign number
      id: `${car.id}_${recall.campaign_number}_${index}`,
      vin: car.vin || '',
      carYear: car.car_year || 0,
      makeName: car.make_name || '',
      modelName: car.model_name || '',
      seller_name: car.service_provider_name || '',
      seller_address: `${car.seller_street || ''}, ${car.seller_city || ''}, ${
        car.seller_region || ''
      } ${car.seller_postal_code || ''}`.trim(),
      campaign_number: recall.campaign_number || '',
      recall_title: recall.title || '',
      dealer_street: car.seller_street || '',
      dealer_city: car.seller_city || '',
      dealer_region: car.seller_region || '',
      dealer_postal_code: car.seller_postal_code || '',
      dealer_latitude: car.seller_latitude || null,
      dealer_longitude: car.seller_longitude || null,
      dealer_phone: car.phone_number || '', // Updated field name
      dealer_type: car.seller_type || 'DEALER',
      dealer_service_provider_id: '', // Not available in materialized view
      dealer_inventory_url: car.detail_url || '', // Updated field name
      match_confidence: 'exact', // Static value as not available in DB
      campaign_skill_level: '', // Handled by client-side storage

      // Additional fields that might be useful
      recall_type: recall.type || '',
      recall_status: recall.status || '',
      recall_description: recall.description || '',
      recall_safety_risk: recall.safety_risk || '',
      recall_remedy: recall.remedy || '',
      car_trim: car.trim_name || '',
      car_price: car.price || null,
      car_mileage: car.mileage || null,
      dealer_rating: car.seller_rating || null,
      safety_recall_count: car.safety_recall_count || 0,
      created_at: car.created_at || null,

      // New fields available in materialized view
      listing_title: car.listing_title || '',
      exterior_color: car.exterior_color_name || '',
      transmission: car.localized_transmission || '',
      engine: car.localized_engine_display_name || '',
      body_type: car.body_type_name || '',
      main_picture_url: car.main_picture_url || '',
      picture_count: car.picture_count || 0,
      possible_recall: car.possible_recall || false,
      is_relevant_recall: car.is_relevant_recall || false,
      sales_status: car.sales_status || '',
      sold_date: car.sold_date || null,
      has_advance_notice_recalls: (() => {
        // Handle advance_notice flag from safety_recalls JSON in prod.cars_with_safety_recalls_mv
        // The value is 'Y' or 'N' as a string
        const advanceNotice = recall.advance_notice;

        // Handle boolean values
        if (typeof advanceNotice === 'boolean') {
          return advanceNotice;
        }

        // Handle string values ('Y' or 'N' from database)
        if (typeof advanceNotice === 'string') {
          const upperValue = advanceNotice.toUpperCase().trim();
          // Return true for 'Y', false for 'N' or any other value
          return upperValue === 'Y';
        }

        // Default to false if missing or unexpected type
        return false;
      })(),

      // Additional fields for compatibility
      priority: (() => {
        // Get priority (skill_level) from campaign preferences
        if (campaignPreferences && recall.campaign_number) {
          const campaignPref = campaignPreferences[recall.campaign_number];
          return campaignPref?.skill_level || 3; // Default to low (3) if not set
        }
        return 3; // Default to low priority if no preferences available
      })(),
      mobile_eligible: (() => {
        // Get mobile eligible from campaign preferences
        if (campaignPreferences && recall.campaign_number) {
          const campaignPref = campaignPreferences[recall.campaign_number];
          return campaignPref?.mobile_eligible || false;
        }
        return false;
      })(),
      coordinates:
        car.seller_latitude && car.seller_longitude
          ? {
            /* eslint-disable indent */
              latitude: car.seller_latitude,
              longitude: car.seller_longitude
              /* eslint-enable indent */
          }
          : undefined,

      // Legacy field names for backward compatibility
      make: car.make_name || '',
      model: car.model_name || '',
      year: car.car_year || 0,
      seller_city: car.seller_city || '',
      seller_region: car.seller_region || '',
      seller_postal_code: car.seller_postal_code || '',
      campaign_title: recall.title || '',
      recall_date: car.created_at || ''
    }));
  });

  // Store in cache
  transformCache.set(cacheKey, {
    data: transformed,
    timestamp: Date.now()
  } as CacheEntry);

  // Clean old entries periodically
  if (transformCache.size > CACHE_MAX_SIZE) {
    cleanExpiredCache();
  }

  return transformed;
};

// Legacy function for backward compatibility (keeping the old approach as fallback)
export const transformSupabaseDataToAppFormat = (recallDetails: any[], cars: any[]) => {
  return recallDetails
    .filter(recall => {
      // Filter out M and N campaigns (maintenance campaigns, not safety recalls)
      const campaignNumber = recall.campaign_number || '';
      return !campaignNumber.includes('M') && !campaignNumber.includes('N');
    })
    .map(recall => {
      // Find corresponding car data
      const car = cars.find(c => c.id === recall.car_id);
      if (!car) {
        return null;
      }

      // Transform to match the CSV structure expected by the app
      return {
        id: recall.id, // Use recall ID as primary identifier
        vin: car.vin || '',
        carYear: car.car_year || 0,
        makeName: car.make_name || '',
        modelName: car.model_name || '',
        seller_name: car.service_provider_name || '',
        seller_address: `${car.seller_street || ''}, ${car.seller_city || ''}, ${
          car.seller_region || ''
        } ${car.seller_postal_code || ''}`.trim(),
        campaign_number: recall.campaign_number || '',
        recall_title: recall.title || '',
        dealer_street: car.seller_street || '',
        dealer_city: car.seller_city || '',
        dealer_region: car.seller_region || '',
        dealer_postal_code: car.seller_postal_code || '',
        dealer_latitude: car.seller_latitude || null,
        dealer_longitude: car.seller_longitude || null,
        dealer_phone: car.phone_number || '', // Updated field name
        dealer_type: car.seller_type || 'DEALER',
        dealer_service_provider_id: '', // Not available in materialized view
        dealer_inventory_url: car.detail_url || '', // Updated field name
        match_confidence: 'exact', // Static value as not available in DB
        campaign_skill_level: '', // Handled by client-side storage

        // Additional fields that might be useful
        recall_type: recall.type || '',
        recall_status: recall.status || '',
        recall_description: recall.description || '',
        car_trim: car.trim_name || '',
        car_price: car.price || null,
        car_mileage: car.mileage || null,
        dealer_rating: car.seller_rating || null,
        created_at: recall.created_at || null
      };
    })
    .filter(Boolean); // Remove null entries where car wasn't found
};
